/**
 * TMS Entity Creation Progress — Tree view of project -> modules -> suites -> cases.
 *
 * Shows entities being created in real-time with animated entry,
 * status icons, and clickable links.
 */

import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Chip,
  Collapse,
} from '@mui/material';
import {
  Folder as ProjectIcon,
  GridView as ModuleIcon,
  Layers as SuiteIcon,
  CheckBox as CaseIcon,
  PlayArrow as RunIcon,
  CheckCircle as CreatedIcon,
  Error as FailedIcon,
  HourglassEmpty as CreatingIcon,
} from '@mui/icons-material';
import { TMSEntityRef } from '../types';

const ENTITY_ICONS: Record<string, React.ReactNode> = {
  project: <ProjectIcon fontSize="small" color="primary" />,
  module: <ModuleIcon fontSize="small" color="info" />,
  test_suite: <SuiteIcon fontSize="small" color="secondary" />,
  test_case: <CaseIcon fontSize="small" color="success" />,
  test_run: <RunIcon fontSize="small" color="warning" />,
};

const STATUS_ICONS: Record<string, React.ReactNode> = {
  creating: <CreatingIcon fontSize="inherit" color="action" />,
  created: <CreatedIcon fontSize="inherit" color="success" />,
  failed: <FailedIcon fontSize="inherit" color="error" />,
};

// Indentation levels by entity type
const INDENT: Record<string, number> = {
  project: 0,
  module: 1,
  test_suite: 2,
  test_case: 3,
  test_run: 2,
};

interface TMSEntityCreationProgressProps {
  entities: TMSEntityRef[];
}

const TMSEntityCreationProgress: React.FC<TMSEntityCreationProgressProps> = ({
  entities,
}) => {
  if (!entities || entities.length === 0) {
    return null;
  }

  // Count by type
  const counts: Record<string, number> = {};
  entities.forEach((e) => {
    counts[e.type] = (counts[e.type] || 0) + 1;
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Typography variant="caption" fontWeight={600}>
          TMS Entities ({entities.length})
        </Typography>
        {Object.entries(counts).map(([type, count]) => (
          <Chip
            key={type}
            size="small"
            label={`${count} ${type.replace('_', ' ')}${count > 1 ? 's' : ''}`}
            variant="outlined"
            sx={{ height: 18, fontSize: '0.65rem' }}
          />
        ))}
      </Box>

      <List dense disablePadding sx={{ maxHeight: 200, overflowY: 'auto' }}>
        {entities.map((entity, idx) => {
          const indent = INDENT[entity.type] || 0;
          return (
            <Collapse in key={`${entity.type}-${entity.id}-${idx}`}>
              <ListItem
                disablePadding
                sx={{
                  pl: indent * 2,
                  py: 0.25,
                  minHeight: 28,
                }}
              >
                <ListItemIcon sx={{ minWidth: 24 }}>
                  {ENTITY_ICONS[entity.type] || <CaseIcon fontSize="small" />}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption" noWrap sx={{ maxWidth: 200 }}>
                        {entity.name}
                      </Typography>
                      <Box sx={{ fontSize: '0.7rem', display: 'flex', alignItems: 'center' }}>
                        {STATUS_ICONS[entity.status] || STATUS_ICONS.created}
                      </Box>
                    </Box>
                  }
                  sx={{ m: 0 }}
                />
              </ListItem>
            </Collapse>
          );
        })}
      </List>
    </Box>
  );
};

export default TMSEntityCreationProgress;
